import { useState, useEffect, useMemo } from 'react';
import { useRebusSession } from '../../../context/RebusSessionContext';
import { getRebus } from '../../../services/rebusService';
import {
  startRebusSession,
  endRebusSession,
  approveExtraTask,
  approveStationAnswer,
  scoreChallenge,
  assignBonusPoints,
  setTeamStartStation,
  resetExtraTaskApproval,
  resetStationAnswerApproval,
} from '../../../services/rebusSessionService';
import { recalculateTeamScore } from '../../../services/rebusTeamService';
import QRCode from '../../common/QRCode';
import RebusScoreboard from './RebusScoreboard';

const TABS = [
  { id: 'teams', label: 'Lag', icon: 'groups' },
  { id: 'activity', label: 'Aktivitet', icon: 'notifications' },
  { id: 'submissions', label: 'Submissions', icon: 'photo_library' },
  { id: 'approvals', label: 'Godkjenning', icon: 'task_alt' },
  { id: 'scoreboard', label: 'Scoreboard', icon: 'leaderboard' },
];

export default function RebusSessionControl({ onBack }) {
  const { session, loading: sessionLoading, error: sessionError } = useRebusSession();
  const [rebus, setRebus] = useState(null);
  const [rebusLoading, setRebusLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(session?.status === 'finished' ? 'scoreboard' : 'teams');
  const [actionLoading, setActionLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Fetch rebus data
  useEffect(() => {
    if (session?.rebusId) {
      setRebusLoading(true);
      getRebus(session.rebusId)
        .then((data) => {
          setRebus(data);
          setRebusLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching rebus:', err);
          setRebusLoading(false);
        });
    }
  }, [session?.rebusId]);

  // Derived data
  const teams = useMemo(() => {
    if (!session?.teams) return [];
    return Object.values(session.teams).sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));
  }, [session?.teams]);

  const stationCount = rebus?.stations?.length || 0;

  const pendingApprovals = useMemo(() => {
    if (!session?.teams) return [];
    const pending = [];
    for (const team of Object.values(session.teams)) {
      // Extra task submissions
      const extra = team.extraTaskResults || {};
      for (const [taskId, result] of Object.entries(extra)) {
        if (result.approved === null) {
          const taskDef = rebus?.extraTasks?.find(t => t.id === taskId);
          pending.push({
            type: 'extra',
            teamId: team.id,
            teamName: team.name,
            taskId,
            taskText: taskDef?.text || taskId,
            taskPoints: taskDef?.points || 2,
            answer: result.answer,
            photoUrl: result.photoUrl,
            comment: result.comment,
            submittedAt: result.submittedAt,
          });
        }
      }
      // Station answer submissions
      const stationResults = team.stationResults || {};
      for (const [stationId, stationResult] of Object.entries(stationResults)) {
        const answers = stationResult.answers || {};
        for (const [questionId, answerData] of Object.entries(answers)) {
          if (answerData.isCorrect === null && answerData.approved === undefined) {
            const stationDef = rebus?.stations?.find(s => s.id === stationId);
            const questionDef = stationDef?.questions?.find(q => q.id === questionId);
            pending.push({
              type: 'station',
              teamId: team.id,
              teamName: team.name,
              taskId: `${stationId}__${questionId}`,
              stationId,
              questionId,
              taskText: questionDef?.text || questionId,
              stationName: stationDef?.name || stationId,
              taskPoints: questionDef?.points || 1,
              answer: answerData.answer,
              submittedAt: answerData.submittedAt,
            });
          }
        }
      }
      // Challenge comments (submitted by station leaders)
      for (const [stationId, stationResult] of Object.entries(stationResults)) {
        if (stationResult.challengeComment && typeof stationResult.challengeScore !== 'number') {
          const stationDef = rebus?.stations?.find(s => s.id === stationId);
          pending.push({
            type: 'challenge',
            teamId: team.id,
            teamName: team.name,
            taskId: `challenge__${stationId}`,
            stationId,
            taskText: stationDef?.challenge?.title || 'Challenge',
            stationName: stationDef?.name || stationId,
            taskPoints: stationDef?.challenge?.maxPoints || 10,
            answer: stationResult.challengeComment,
            comment: `Rapportert av ${stationResult.challengeCommentBy || 'stasjonsleiar'}`,
            submittedAt: stationResult.challengeCommentAt,
          });
        }
      }
    }
    return pending.sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));
  }, [session?.teams, rebus?.extraTasks, rebus?.stations]);

  const allApprovalItems = useMemo(() => {
    if (!session?.teams) return [];
    const items = [];
    for (const team of Object.values(session.teams)) {
      // Extra task submissions
      const extra = team.extraTaskResults || {};
      for (const [taskId, result] of Object.entries(extra)) {
        const taskDef = rebus?.extraTasks?.find(t => t.id === taskId);
        items.push({
          type: 'extra',
          teamId: team.id,
          teamName: team.name,
          taskId,
          taskText: taskDef?.text || taskId,
          taskPoints: taskDef?.points || 2,
          answer: result.answer,
          photoUrl: result.photoUrl,
          comment: result.comment,
          submittedAt: result.submittedAt,
          approved: result.approved,
        });
      }
      // Station answer submissions
      const stationResults = team.stationResults || {};
      for (const [stationId, stationResult] of Object.entries(stationResults)) {
        const answers = stationResult.answers || {};
        for (const [questionId, answerData] of Object.entries(answers)) {
          const stationDef = rebus?.stations?.find(s => s.id === stationId);
          const questionDef = stationDef?.questions?.find(q => q.id === questionId);
          const isAutoScored = answerData.isCorrect !== null && answerData.approved === undefined;
          items.push({
            type: 'station',
            teamId: team.id,
            teamName: team.name,
            taskId: `${stationId}__${questionId}`,
            stationId,
            questionId,
            taskText: questionDef?.text || questionId,
            stationName: stationDef?.name || stationId,
            taskPoints: questionDef?.points || 1,
            answer: answerData.answer,
            submittedAt: answerData.submittedAt,
            approved: isAutoScored ? answerData.isCorrect : (answerData.approved ?? null),
          });
        }
      }
      // Challenge comments
      for (const [stationId, stationResult] of Object.entries(stationResults)) {
        if (stationResult.challengeComment) {
          const stationDef = rebus?.stations?.find(s => s.id === stationId);
          items.push({
            type: 'challenge',
            teamId: team.id,
            teamName: team.name,
            taskId: `challenge__${stationId}`,
            stationId,
            taskText: stationDef?.challenge?.title || 'Challenge',
            stationName: stationDef?.name || stationId,
            taskPoints: stationDef?.challenge?.maxPoints || 10,
            answer: stationResult.challengeComment,
            comment: `Rapportert av ${stationResult.challengeCommentBy || 'stasjonsleiar'}`,
            submittedAt: stationResult.challengeCommentAt,
            approved: typeof stationResult.challengeScore === 'number' ? true : null,
          });
        }
      }
    }
    return items.sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));
  }, [session?.teams, rebus?.extraTasks, rebus?.stations]);

  const activityFeed = useMemo(() => {
    if (!session?.teams) return [];
    const entries = [];
    for (const team of Object.values(session.teams)) {
      // Station answers
      const stationResults = team.stationResults || {};
      for (const [stationId, stationResult] of Object.entries(stationResults)) {
        const answers = stationResult.answers || {};
        for (const [questionId, answerData] of Object.entries(answers)) {
          entries.push({
            type: 'answer',
            teamName: team.name,
            stationId,
            questionId,
            answer: answerData.answer,
            isCorrect: answerData.isCorrect,
            points: answerData.points,
            timestamp: answerData.submittedAt,
          });
        }
        // Station completion
        if (stationResult.completedAt) {
          entries.push({
            type: 'station_complete',
            teamName: team.name,
            stationId,
            timestamp: stationResult.completedAt,
          });
        }
        // Challenge score
        if (typeof stationResult.challengeScore === 'number') {
          entries.push({
            type: 'challenge',
            teamName: team.name,
            stationId,
            score: stationResult.challengeScore,
            scoredBy: stationResult.challengeScoredBy,
            timestamp: stationResult.challengeScoredAt,
          });
        }
      }
      // Extra task submissions
      const extraResults = team.extraTaskResults || {};
      for (const [taskId, result] of Object.entries(extraResults)) {
        entries.push({
          type: 'extra_task',
          teamName: team.name,
          taskId,
          answer: result.answer,
          photoUrl: result.photoUrl,
          approved: result.approved,
          timestamp: result.submittedAt,
        });
      }
    }
    return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [session?.teams]);

  // Handlers
  const handleStartRebus = async () => {
    if (!session?.id) return;
    setActionLoading(true);
    try {
      await startRebusSession(session.id);
    } catch (err) {
      console.error('Error starting rebus:', err);
      alert('Klarte ikkje starte Mikkelchase');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndRebus = async () => {
    if (!session?.id) return;
    const confirmed = window.confirm('Er du sikker på at du vil avslutte Mikkelchase?');
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await endRebusSession(session.id);
    } catch (err) {
      console.error('Error ending rebus:', err);
      alert('Klarte ikkje avslutte Mikkelchase');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (teamId, taskId, points, adminFeedback = '', itemType = 'extra') => {
    try {
      if (itemType === 'challenge' && taskId.startsWith('challenge__')) {
        const stationId = taskId.replace('challenge__', '');
        await scoreChallenge(session.id, teamId, stationId, points, 'Admin');
      } else if (itemType === 'station' && taskId.includes('__')) {
        const [stationId, questionId] = taskId.split('__');
        await approveStationAnswer(session.id, teamId, stationId, questionId, true, points, adminFeedback);
      } else {
        await approveExtraTask(session.id, teamId, taskId, true, points, adminFeedback);
      }
      await recalculateTeamScore(session.id, teamId);
    } catch (err) {
      console.error('Error approving task:', err);
      alert('Klarte ikkje godkjenne oppgava');
    }
  };

  const handleReject = async (teamId, taskId, adminFeedback = '', itemType = 'extra') => {
    try {
      if (itemType === 'station' && taskId.includes('__')) {
        const [stationId, questionId] = taskId.split('__');
        await approveStationAnswer(session.id, teamId, stationId, questionId, false, 0, adminFeedback);
      } else {
        await approveExtraTask(session.id, teamId, taskId, false, 0, adminFeedback);
      }
      await recalculateTeamScore(session.id, teamId);
    } catch (err) {
      console.error('Error rejecting task:', err);
      alert('Klarte ikkje avvise oppgava');
    }
  };

  const handleResetApproval = async (teamId, taskId, itemType = 'extra') => {
    try {
      if (itemType === 'station' && taskId.includes('__')) {
        const [stationId, questionId] = taskId.split('__');
        await resetStationAnswerApproval(session.id, teamId, stationId, questionId);
      } else {
        await resetExtraTaskApproval(session.id, teamId, taskId);
      }
      await recalculateTeamScore(session.id, teamId);
    } catch (err) {
      console.error('Error resetting approval:', err);
      alert('Klarte ikkje angre');
    }
  };

  const handleBulkApprove = async () => {
    if (pendingApprovals.length === 0) return;
    const confirmed = window.confirm(`Godkjenn alle ${pendingApprovals.length} innleveringar?`);
    if (!confirmed) return;
    for (const item of pendingApprovals) {
      try {
        await handleApprove(item.teamId, item.taskId, item.taskPoints, '', item.type);
      } catch (err) {
        console.error('Error bulk approving:', err);
      }
    }
  };

  const handleBonus = async (teamId, points, reason) => {
    try {
      await assignBonusPoints(session.id, teamId, points, reason);
      await recalculateTeamScore(session.id, teamId);
    } catch (err) {
      console.error('Error assigning bonus:', err);
      alert('Klarte ikkje gi bonuspoeng');
    }
  };

  const handleImageClick = (url) => {
    setFullscreenImage(url);
  };

  // Loading / Error states
  if (sessionLoading || rebusLoading) {
    return (
      <div className="min-h-screen bg-background-light font-display flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Lastar rebus-okt...</p>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="min-h-screen bg-background-light font-display flex items-center justify-center p-4">
        <div className="bg-red-50 shadow-ambient rounded-2xl p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-red-800 mb-2">Feil</h2>
          <p className="text-red-700 mb-4">{sessionError}</p>
          <button onClick={onBack} className="px-6 py-3 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white rounded-xl font-bold hover:brightness-110 transition-all">
            Tilbake
          </button>
        </div>
      </div>
    );
  }

  if (!session || !rebus) return null;

  // If finished, default to scoreboard tab
  // (but allow switching to other tabs to review answers, photos, etc.)

  return (
    <div className="min-h-screen bg-background-light font-display text-[#0d121b] pb-28">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-light/95 backdrop-blur-sm shadow-ambient">
        <div className="flex items-center justify-between p-4">
          <button onClick={onBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors">
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          </button>
          <div className="text-center flex-1 min-w-0">
            <h1 className="text-base font-bold truncate">{rebus.title}</h1>
            <p className="text-xs text-gray-500">
              {session.status === 'waiting' ? 'Ventar pa lag' : 'Aktiv'}
            </p>
          </div>
          <button
            onClick={() => setShowQR(!showQR)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold hover:bg-emerald-200 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">{showQR ? 'close' : 'qr_code'}</span>
            <span className="hidden sm:inline text-emerald-600 font-medium">Del PIN</span>
            {session.id}
          </button>
        </div>

        {/* QR Code Panel */}
        {showQR && (
          <div className="px-4 pb-4 flex flex-col items-center gap-3 bg-white">
            <QRCode sessionId={session.id} size={160} joinPath="/mikkelchase/join" />
            <div className="text-center">
              <p className="text-xs text-gray-500">PIN-kode</p>
              <p className="text-3xl font-extrabold tracking-widest text-emerald-700">{session.id}</p>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors relative min-w-0 ${
                activeTab === tab.id
                  ? 'text-emerald-700'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <span className="material-symbols-outlined text-[20px]">{tab.icon}</span>
              <span className="truncate text-[10px] sm:text-xs">{tab.label}</span>
              {tab.id === 'approvals' && pendingApprovals.length > 0 && (
                <span className="absolute top-1.5 right-1/4 size-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {pendingApprovals.length}
                </span>
              )}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-emerald-600 rounded-full"></div>
              )}
            </button>
          ))}
        </div>
      </header>

      {/* Tab Content */}
      <div className="max-w-3xl mx-auto">
        {activeTab === 'teams' && (
          <TeamsTab teams={teams} rebus={rebus} stationCount={stationCount} session={session} onBonus={handleBonus} onSetStartStation={(teamId, stationId) => setTeamStartStation(session.id, teamId, stationId)} />
        )}
        {activeTab === 'activity' && (
          <ActivityTab feed={activityFeed} rebus={rebus} onImageClick={handleImageClick} />
        )}
        {activeTab === 'submissions' && (
          <SubmissionsTab session={session} rebus={rebus} onImageClick={handleImageClick} onApprove={handleApprove} onReject={handleReject} />
        )}
        {activeTab === 'approvals' && (
          <ApprovalsTab
            approvals={pendingApprovals}
            allItems={allApprovalItems}
            onApprove={handleApprove}
            onReject={handleReject}
            onBulkApprove={handleBulkApprove}
            onReset={handleResetApproval}
            onImageClick={handleImageClick}
            onBonus={handleBonus}
          />
        )}
        {activeTab === 'scoreboard' && (
          <div className="p-4">
            <RebusScoreboard session={session} rebus={rebus} embedded />
          </div>
        )}
      </div>

      {/* Bottom Control Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-background-light/95 backdrop-blur-sm shadow-[0_-4px_16px_rgba(0,0,0,0.06)] p-4">
        <div className="max-w-3xl mx-auto flex gap-3">
          {session.status === 'waiting' && (
            <button
              onClick={handleStartRebus}
              disabled={actionLoading || teams.length === 0}
              className="flex-1 py-3 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 disabled:bg-gray-300 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition-all"
            >
              <span className="material-symbols-outlined text-[20px]">play_arrow</span>
              Start Mikkelchase ({teams.length} lag)
            </button>
          )}
          {session.status === 'active' && (
            <button
              onClick={handleEndRebus}
              disabled={actionLoading}
              className="flex-1 py-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition-colors"
            >
              <span className="material-symbols-outlined text-[20px]">stop</span>
              Avslutt Mikkelchase
            </button>
          )}
          {session.status === 'finished' && (
            <button
              onClick={onBack}
              className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition-colors"
            >
              <span className="material-symbols-outlined text-[20px]">arrow_back</span>
              Tilbake til oversikta
            </button>
          )}
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <ImageModal imageUrl={fullscreenImage} onClose={() => setFullscreenImage(null)} />
      )}
    </div>
  );
}

// -- Teams Tab --
function TeamsTab({ teams, rebus, stationCount, session, onBonus, onSetStartStation }) {
  const [bonusTeamId, setBonusTeamId] = useState(null);
  const [bonusPoints, setBonusPoints] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusLoading, setBonusLoading] = useState(false);

  const getTeamStation = (team) => {
    const completed = Object.keys(team.stationResults || {}).filter(
      sid => team.stationResults[sid]?.completedAt
    );
    if (completed.length >= stationCount) return 'Ferdig';
    return `Stasjon ${completed.length + 1} av ${stationCount}`;
  };

  const getCompletedCount = (team) => {
    return Object.keys(team.stationResults || {}).filter(
      sid => team.stationResults[sid]?.completedAt
    ).length;
  };

  const handleBonusSubmit = async (teamId) => {
    const pts = parseInt(bonusPoints, 10);
    if (isNaN(pts) || pts === 0) return;
    setBonusLoading(true);
    try {
      await onBonus(teamId, pts, bonusReason);
      setBonusTeamId(null);
      setBonusPoints('');
      setBonusReason('');
    } finally {
      setBonusLoading(false);
    }
  };

  // Station leaders
  const stationLeaders = session?.stationLeaders || {};

  return (
    <div className="p-4 space-y-4">
      {/* Team count */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Lag ({teams.length})</h3>
      </div>

      {teams.length === 0 ? (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">groups</span>
          <p className="text-gray-500 font-medium">Ingen lag har blitt med enno</p>
          <p className="text-gray-400 text-sm mt-1">Del PIN-koden for å la lag bli med</p>
        </div>
      ) : (
        <div className="space-y-2">
          {teams.map((team) => {
            const completed = getCompletedCount(team);
            const progress = stationCount > 0 ? (completed / stationCount) * 100 : 0;
            const isExpanded = bonusTeamId === team.id;
            const bonusHistory = team.bonusHistory || [];

            return (
              <div key={team.id} className="bg-white rounded-xl shadow-ambient p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm shrink-0">
                      {team.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#0d121b] truncate">{team.name}</p>
                      <p className="text-xs text-gray-500">{getTeamStation(team)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(team.bonusPoints || 0) !== 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        team.bonusPoints > 0
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {team.bonusPoints > 0 ? '+' : ''}{team.bonusPoints} bonus
                      </span>
                    )}
                    <div className="text-right shrink-0">
                      <p className="text-xl font-extrabold text-emerald-700">{team.totalScore || 0}</p>
                      <p className="text-[10px] text-gray-400 uppercase font-bold">poeng</p>
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>

                {/* Members list */}
                {team.members?.length > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1.5 truncate">
                    {team.members.join(', ')}
                  </p>
                )}

                {/* Start station selector */}
                {rebus?.stations?.length > 1 && session?.status === 'waiting' && onSetStartStation && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 font-medium shrink-0">Start:</span>
                    <select
                      value={team.startStationId || ''}
                      onChange={(e) => onSetStartStation(team.id, e.target.value || null)}
                      className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 font-medium focus:ring-2 focus:ring-emerald-300 outline-none flex-1 min-w-0"
                    >
                      <option value="">Stasjon 1 (standard)</option>
                      {rebus.stations.map((s) => (
                        <option key={s.id} value={s.id}>{s.name || `Stasjon ${s.order}`}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Bonus button */}
                <button
                  type="button"
                  onClick={() => setBonusTeamId(isExpanded ? null : team.id)}
                  className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-emerald-700 font-medium transition-colors"
                >
                  <span className="material-symbols-outlined text-[16px]">
                    {isExpanded ? 'expand_less' : 'stars'}
                  </span>
                  {isExpanded ? 'Lukk' : 'Bonuspoeng'}
                </button>

                {/* Bonus form */}
                {isExpanded && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-xl space-y-2">
                    <label className="text-xs font-bold text-gray-600 uppercase">Gi bonuspoeng</label>
                    <input
                      type="number"
                      value={bonusPoints}
                      onChange={(e) => setBonusPoints(e.target.value)}
                      placeholder="t.d. +300 eller -200"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                    />
                    <label className="text-xs font-bold text-gray-600 uppercase">Grunn (valfritt)</label>
                    <textarea
                      value={bonusReason}
                      onChange={(e) => setBonusReason(e.target.value)}
                      placeholder="t.d. Beste kreative svar!"
                      maxLength={500}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-gray-400">{bonusReason.length} / 500</span>
                      <button
                        type="button"
                        onClick={() => handleBonusSubmit(team.id)}
                        disabled={bonusLoading || !bonusPoints || parseInt(bonusPoints, 10) === 0}
                        className="px-4 py-2 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white rounded-lg text-sm font-bold hover:brightness-110 disabled:opacity-50 transition-all"
                      >
                        {bonusLoading ? 'Lagrar...' : 'Gi poeng'}
                      </button>
                    </div>

                    {/* Bonus history */}
                    {bonusHistory.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] font-bold text-gray-400 uppercase">
                            Historikk ({bonusHistory.length})
                          </p>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">
                            Sum: {team.bonusPoints > 0 ? '+' : ''}{team.bonusPoints}
                          </p>
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                          {[...bonusHistory].reverse().map((entry, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                              <span className={`font-bold ${entry.points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {entry.points > 0 ? '+' : ''}{entry.points}
                              </span>
                              {entry.reason && <span className="text-gray-400 truncate">{entry.reason}</span>}
                              <span className="text-gray-300 text-[10px] ml-auto shrink-0">
                                {new Date(entry.assignedAt).toLocaleTimeString('nn-NO', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Station Leaders */}
      {Object.keys(stationLeaders).length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-2">Stasjonsledarar</h4>
          <div className="space-y-1">
            {Object.entries(stationLeaders).map(([stationId, leader]) => {
              const stationDef = rebus?.stations?.find(s => s.id === stationId);
              return (
                <div key={stationId} className="flex items-center gap-2 text-sm text-gray-600 bg-white rounded-lg px-3 py-2 shadow-ambient">
                  <span className="material-symbols-outlined text-[16px] text-emerald-600">pin_drop</span>
                  <span className="font-medium">{stationDef?.name || stationId}</span>
                  <span className="text-gray-400">—</span>
                  <span>{leader.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// -- Activity Tab --
function ActivityTab({ feed, rebus, onImageClick }) {
  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('nn-NO', { hour: '2-digit', minute: '2-digit' });
  };

  const getStationName = (stationId) => {
    const station = rebus?.stations?.find(s => s.id === stationId);
    return station?.name || stationId;
  };

  const getExtraTaskTitle = (taskId) => {
    const task = rebus?.extraTasks?.find(t => t.id === taskId);
    return task?.text || taskId;
  };

  const getIcon = (entry) => {
    switch (entry.type) {
      case 'answer': return entry.isCorrect ? 'check_circle' : entry.isCorrect === false ? 'cancel' : 'help';
      case 'station_complete': return 'flag';
      case 'challenge': return 'emoji_events';
      case 'extra_task': return 'star';
      default: return 'circle';
    }
  };

  const getIconColor = (entry) => {
    switch (entry.type) {
      case 'answer': return entry.isCorrect ? 'text-emerald-500' : entry.isCorrect === false ? 'text-red-400' : 'text-gray-400';
      case 'station_complete': return 'text-blue-500';
      case 'challenge': return 'text-purple-500';
      case 'extra_task': return 'text-amber-500';
      default: return 'text-gray-400';
    }
  };

  const getDescription = (entry) => {
    switch (entry.type) {
      case 'answer':
        return `svarte pa sporsmal ${entry.questionId} (${getStationName(entry.stationId)})`;
      case 'station_complete':
        return `fullforte ${getStationName(entry.stationId)}`;
      case 'challenge':
        return `fekk ${entry.score} poeng for utfordring (${getStationName(entry.stationId)})`;
      case 'extra_task':
        return `leverte "${getExtraTaskTitle(entry.taskId)}"`;
      default:
        return '';
    }
  };

  return (
    <div className="p-4">
      {feed.length === 0 ? (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">notifications</span>
          <p className="text-gray-500 font-medium">Ingen aktivitet enno</p>
        </div>
      ) : (
        <div className="space-y-3">
          {feed.map((entry, i) => (
            <div key={i} className="bg-white rounded-xl shadow-ambient p-4">
              <div className="flex items-start gap-3">
                <span className={`material-symbols-outlined text-[20px] mt-0.5 shrink-0 ${getIconColor(entry)}`}>
                  {getIcon(entry)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-bold">{entry.teamName}</span>{' '}
                    <span className="text-gray-600">{getDescription(entry)}</span>
                  </p>
                  {entry.type === 'extra_task' && (
                    <p className="text-xs font-bold text-gray-700 mt-1">
                      {getExtraTaskTitle(entry.taskId)}
                    </p>
                  )}
                </div>
                <span className="text-[10px] text-gray-400 shrink-0 mt-0.5">{formatTime(entry.timestamp)}</span>
              </div>

              {/* Text answer as quote block */}
              {entry.answer && (
                <div className="mt-3 border-l-4 border-emerald-300 bg-emerald-50/50 rounded-r-xl px-4 py-3">
                  <p className="text-sm text-gray-800 italic">{entry.answer}</p>
                </div>
              )}

              {/* Large clickable image */}
              {entry.photoUrl && (
                <div className="mt-3">
                  <img
                    src={entry.photoUrl}
                    alt="Innlevert bilete"
                    className="w-full max-h-64 object-cover rounded-xl shadow-ambient cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => onImageClick(entry.photoUrl)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -- Submissions Tab (GooseChase-style) --
function SubmissionsTab({ session, rebus, onImageClick, onApprove, onReject }) {
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [feedbackTexts, setFeedbackTexts] = useState({});

  // Build submission data per extra task
  const taskSubmissions = useMemo(() => {
    if (!rebus?.extraTasks || !session?.teams) return [];

    return rebus.extraTasks.map(task => {
      const submissions = [];
      for (const team of Object.values(session.teams)) {
        const result = team.extraTaskResults?.[task.id];
        if (result) {
          submissions.push({
            teamId: team.id,
            teamName: team.name,
            answer: result.answer,
            photoUrl: result.photoUrl,
            approved: result.approved,
            submittedAt: result.submittedAt,
          });
        }
      }
      submissions.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

      return {
        taskId: task.id,
        taskName: task.name || null,
        taskText: task.text,
        taskPoints: task.points || 2,
        submissions,
        thumbnailUrl: submissions.find(s => s.photoUrl)?.photoUrl || null,
      };
    });
  }, [rebus?.extraTasks, session?.teams]);

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('nn-NO', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${d.toLocaleDateString('nn-NO', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('nn-NO', { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Drill-down view for a single task
  if (selectedTaskId) {
    const taskData = taskSubmissions.find(t => t.taskId === selectedTaskId);
    if (!taskData) {
      setSelectedTaskId(null);
      return null;
    }

    return (
      <div className="p-4">
        <button
          onClick={() => setSelectedTaskId(null)}
          className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors mb-4"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-sm font-medium">Tilbake til oppgaver</span>
        </button>

        <div className="mb-4">
          <h3 className="text-lg font-bold">
            {taskData.taskName && <span className="text-teal-600">{taskData.taskName}: </span>}
            {taskData.taskText}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
              {taskData.taskPoints} poeng
            </span>
            <span className="text-xs text-gray-500">
              {taskData.submissions.length} {taskData.submissions.length === 1 ? 'innlevering' : 'innleveringar'}
            </span>
          </div>
        </div>

        {taskData.submissions.length === 0 ? (
          <div className="text-center py-12">
            <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">inbox</span>
            <p className="text-gray-500 font-medium">Ingen innleveringar enno</p>
          </div>
        ) : (
          <div className="space-y-4">
            {taskData.submissions.map((sub, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-ambient p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="size-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs shrink-0">
                      {sub.teamName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <p className="font-bold text-sm">{sub.teamName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {sub.approved === true && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Godkjent</span>
                    )}
                    {sub.approved === false && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">Avvist</span>
                    )}
                    {sub.approved === null && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-bold">Ventar</span>
                    )}
                    <span className="text-[10px] text-gray-400">{formatDate(sub.submittedAt)}</span>
                  </div>
                </div>

                {sub.comment && (
                  <div className="border-l-4 border-blue-300 bg-blue-50/50 rounded-r-xl px-4 py-3">
                    <p className="text-xs text-blue-500 font-semibold mb-0.5">Kommentar frå laget</p>
                    <p className="text-sm text-gray-800 italic">{sub.comment}</p>
                  </div>
                )}

                {sub.photoUrl && (
                  <img
                    src={sub.photoUrl}
                    alt="Innlevert bilete"
                    className="w-full max-h-80 object-cover rounded-xl shadow-ambient cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => onImageClick(sub.photoUrl)}
                  />
                )}

                {sub.answer && (
                  <div className="border-l-4 border-emerald-300 bg-emerald-50/50 rounded-r-xl px-4 py-3">
                    <p className="text-sm text-gray-800 italic">{sub.answer}</p>
                  </div>
                )}

                {/* Approve/reject buttons for pending submissions */}
                {sub.approved === null && onApprove && onReject && (
                  <div className="space-y-2 pt-1">
                    <textarea
                      value={feedbackTexts[`${sub.teamId}-${taskData.taskId}`] || ''}
                      onChange={(e) => setFeedbackTexts(prev => ({ ...prev, [`${sub.teamId}-${taskData.taskId}`]: e.target.value }))}
                      placeholder="Tilbakemelding til laget (valfritt)"
                      maxLength={300}
                      rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          onReject(sub.teamId, taskData.taskId, feedbackTexts[`${sub.teamId}-${taskData.taskId}`] || '', 'extra');
                          setFeedbackTexts(prev => ({ ...prev, [`${sub.teamId}-${taskData.taskId}`]: '' }));
                        }}
                        className="flex-1 flex items-center justify-center gap-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-bold text-sm transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                        Avvis
                      </button>
                      <button
                        onClick={() => {
                          onApprove(sub.teamId, taskData.taskId, taskData.taskPoints, feedbackTexts[`${sub.teamId}-${taskData.taskId}`] || '', 'extra');
                          setFeedbackTexts(prev => ({ ...prev, [`${sub.teamId}-${taskData.taskId}`]: '' }));
                        }}
                        className="flex-1 flex items-center justify-center gap-1 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-bold text-sm transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">check</span>
                        Godkjenn
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Grid view of all tasks
  return (
    <div className="p-4">
      <h3 className="text-lg font-bold mb-4">Oppgaver ({taskSubmissions.length})</h3>

      {taskSubmissions.length === 0 ? (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">photo_library</span>
          <p className="text-gray-500 font-medium">Ingen ekstraoppgaver i denne rebusen</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {taskSubmissions.map(task => (
            <button
              key={task.taskId}
              onClick={() => setSelectedTaskId(task.taskId)}
              className="bg-white rounded-xl shadow-ambient overflow-hidden text-left hover:shadow-lg transition-all group"
            >
              {/* Thumbnail */}
              <div className="aspect-square bg-gray-100 relative overflow-hidden">
                {task.thumbnailUrl ? (
                  <img
                    src={task.thumbnailUrl}
                    alt={task.taskText}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-4xl text-gray-300">photo_camera</span>
                  </div>
                )}
                {/* Points badge */}
                <span className="absolute top-2 right-2 text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold shadow-sm">
                  {task.taskPoints}p
                </span>
              </div>

              {/* Card info */}
              <div className="p-3">
                <p className="text-sm font-bold text-gray-800 line-clamp-2 leading-tight">
                  {task.taskName && <span className="text-teal-600">{task.taskName}: </span>}
                  {task.taskText}
                </p>
                <p className="text-xs text-gray-500 mt-1.5">
                  {task.submissions.length} {task.submissions.length === 1 ? 'innlevering' : 'innleveringar'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -- Approvals Tab --
function ApprovalsTab({ approvals, allItems, onApprove, onReject, onBulkApprove, onReset, onImageClick, onBonus }) {
  const [showFilter, setShowFilter] = useState('pending'); // 'pending' or 'all'
  const [bonusItemKey, setBonusItemKey] = useState(null);
  const [bonusPoints, setBonusPoints] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusLoading, setBonusLoading] = useState(false);
  const [feedbackTexts, setFeedbackTexts] = useState({}); // { 'teamId-taskId': 'feedback text' }

  const getFeedback = (teamId, taskId) => feedbackTexts[`${teamId}-${taskId}`] || '';
  const setFeedback = (teamId, taskId, text) => {
    setFeedbackTexts(prev => ({ ...prev, [`${teamId}-${taskId}`]: text }));
  };

  const displayItems = showFilter === 'pending' ? approvals : allItems;

  const handleBonusSubmit = async (teamId) => {
    const pts = parseInt(bonusPoints, 10);
    if (isNaN(pts) || pts === 0) return;
    setBonusLoading(true);
    try {
      await onBonus(teamId, pts, bonusReason);
      setBonusItemKey(null);
      setBonusPoints('');
      setBonusReason('');
    } finally {
      setBonusLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-3">
      {/* Controls bar */}
      <div className="flex items-center justify-between gap-2">
        {/* Filter toggle */}
        <div className="flex bg-gray-100 rounded-xl p-0.5">
          <button
            onClick={() => setShowFilter('pending')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
              showFilter === 'pending'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Ventande ({approvals.length})
          </button>
          <button
            onClick={() => setShowFilter('all')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
              showFilter === 'all'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Alle ({allItems.length})
          </button>
        </div>

        {/* Bulk approve */}
        {approvals.length > 0 && showFilter === 'pending' && (
          <button
            onClick={onBulkApprove}
            className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white rounded-xl text-xs font-bold transition-all"
          >
            <span className="material-symbols-outlined text-[16px]">done_all</span>
            Godkjenn alle ({approvals.length})
          </button>
        )}
      </div>

      {displayItems.length === 0 ? (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">task_alt</span>
          <p className="text-gray-500 font-medium">
            {showFilter === 'pending'
              ? 'Ingen oppgåver ventar på godkjenning'
              : 'Ingen innleveringar enno'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayItems.map((item) => {
            const isApproved = item.approved === true;
            const isRejected = item.approved === false;
            const isPending = !isApproved && !isRejected;
            const itemKey = `${item.teamId}-${item.taskId}`;
            const showBonusForm = bonusItemKey === itemKey;

            return (
              <div
                key={itemKey}
                className={`bg-white rounded-2xl shadow-ambient p-4 space-y-3 ${
                  isPending ? '' : isApproved ? 'ring-1 ring-emerald-200' : 'ring-1 ring-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-[#0d121b]">{item.teamName}</p>
                    <p className="text-xs text-gray-500">{item.taskText}</p>
                    {item.type === 'station' && item.stationName && (
                      <p className="text-[10px] text-teal-600 font-semibold mt-0.5">Stasjon: {item.stationName}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                      item.type === 'station' ? 'bg-teal-50 text-teal-600' : 'bg-purple-50 text-purple-600'
                    }`}>
                      {item.type === 'station' ? 'Stasjon' : 'Ekstra'}
                    </span>
                    {isApproved && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Godkjent</span>
                    )}
                    {isRejected && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">Avvist</span>
                    )}
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                      {item.taskPoints} poeng
                    </span>
                  </div>
                </div>

                {/* Submission content */}
                {item.answer && (
                  <div className="border-l-4 border-emerald-300 bg-emerald-50/50 rounded-r-xl px-4 py-3">
                    <p className="text-sm text-gray-800 italic">{item.answer}</p>
                  </div>
                )}
                {item.comment && (
                  <div className="border-l-4 border-blue-300 bg-blue-50/50 rounded-r-xl px-4 py-3">
                    <p className="text-xs text-blue-500 font-semibold mb-0.5">Kommentar frå laget</p>
                    <p className="text-sm text-gray-800 italic">{item.comment}</p>
                  </div>
                )}
                {item.photoUrl && (
                  <img
                    src={item.photoUrl}
                    alt="Innlevert bilete"
                    className="w-full rounded-xl shadow-ambient cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => onImageClick(item.photoUrl)}
                  />
                )}

                {/* Admin feedback — shown after review */}
                {!isPending && item.adminFeedback && (
                  <div className="border-l-4 border-amber-400 bg-amber-50/50 rounded-r-xl px-4 py-3">
                    <p className="text-xs text-amber-600 font-semibold mb-0.5">Tilbakemelding frå admin</p>
                    <p className="text-sm text-gray-800 italic">{item.adminFeedback}</p>
                  </div>
                )}

                {/* Action buttons — only for pending items */}
                {isPending && (
                  <div className="space-y-2">
                    <textarea
                      value={getFeedback(item.teamId, item.taskId)}
                      onChange={(e) => setFeedback(item.teamId, item.taskId, e.target.value)}
                      placeholder="Tilbakemelding til laget (valfritt)"
                      maxLength={300}
                      rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          onReject(item.teamId, item.taskId, getFeedback(item.teamId, item.taskId), item.type);
                          setFeedback(item.teamId, item.taskId, '');
                        }}
                        className="flex-1 flex items-center justify-center gap-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-bold text-sm transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                        Avvis
                      </button>
                      <button
                        onClick={() => {
                          onApprove(item.teamId, item.taskId, item.taskPoints, getFeedback(item.teamId, item.taskId), item.type);
                          setFeedback(item.teamId, item.taskId, '');
                        }}
                        className="flex-1 flex items-center justify-center gap-1 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-bold text-sm transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">check</span>
                        Godkjenn
                      </button>
                    </div>
                  </div>
                )}

                {/* Undo + Bonus row */}
                <div className="flex items-center gap-3">
                  {!isPending && onReset && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Angre godkjenning/avvising? Oppgåva blir ventande igjen.')) {
                          onReset(item.teamId, item.taskId, item.type);
                        }
                      }}
                      className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-semibold transition-colors"
                    >
                      <span className="material-symbols-outlined text-[16px]">undo</span>
                      Angre
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setBonusItemKey(showBonusForm ? null : itemKey);
                      setBonusPoints('');
                      setBonusReason('');
                    }}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-emerald-700 font-medium transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      {showBonusForm ? 'expand_less' : 'stars'}
                    </span>
                    {showBonusForm ? 'Lukk' : 'Bonuspoeng'}
                  </button>
                </div>

                {/* Bonus form */}
                {showBonusForm && (
                  <div className="p-3 bg-gray-50 rounded-xl space-y-2">
                    <label className="text-xs font-bold text-gray-600 uppercase">Gi bonuspoeng</label>
                    <input
                      type="number"
                      value={bonusPoints}
                      onChange={(e) => setBonusPoints(e.target.value)}
                      placeholder="t.d. +300 eller -200"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                    />
                    <label className="text-xs font-bold text-gray-600 uppercase">Kommentar (valfritt)</label>
                    <textarea
                      value={bonusReason}
                      onChange={(e) => setBonusReason(e.target.value)}
                      placeholder="t.d. Kreativt svar!"
                      maxLength={500}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-gray-400">{bonusReason.length} / 500</span>
                      <button
                        type="button"
                        onClick={() => handleBonusSubmit(item.teamId)}
                        disabled={bonusLoading || !bonusPoints || parseInt(bonusPoints, 10) === 0}
                        className="px-4 py-2 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white rounded-lg text-sm font-bold hover:brightness-110 disabled:opacity-50 transition-all"
                      >
                        {bonusLoading ? 'Lagrar...' : 'Gi poeng'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -- Image Fullscreen Modal --
function ImageModal({ imageUrl, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
      onClick={onClose}
    >
      <img
        src={imageUrl}
        alt="Fullskjerm bilete"
        className="max-w-full max-h-full object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
      >
        <span className="material-symbols-outlined text-[32px]">close</span>
      </button>
    </div>
  );
}
